import React from "react";
import type {Step, Tour} from "nextstepjs";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import {DASHBOARD_TOUR_NAME} from "./DashboardTour";

export interface TourStepVisibility {
   showInbound: boolean;
   showOutreach: boolean;
   showContacts: boolean;
   showCampaigns: boolean;
   showGailGpt: boolean;
   showGeneralSection: boolean;
   showRecommendedActions: boolean;
}

function tourIcon(name: Parameters<typeof Icon>[0]["icon"]) {
   return <Icon icon={name} type={ICON_TYPE.DUOTONE} />;
}

export function buildDashboardTourSteps(
   t: (key: string) => string,
   visibility: TourStepVisibility
): Tour[] {
   const showCommunicationsSection =
      visibility.showInbound ||
      visibility.showOutreach ||
      visibility.showContacts ||
      visibility.showCampaigns;

   const steps: Step[] = [
      {
         icon: tourIcon("hand-wave"),
         title: t("tour.welcome.title"),
         content: t("tour.welcome.content"),
         showControls: true,
         showSkip: true,
      },
      {
         icon: tourIcon("chart-mixed"),
         title: t("tour.metrics.title"),
         content: t("tour.metrics.content"),
         selector: "#tour-dashboard-metrics",
         side: "bottom",
         showControls: true,
         showSkip: true,
         pointerPadding: 10,
         pointerRadius: 12,
         viewportID: "dashboard-viewport",
      },
   ];

   // The recommended actions section is hidden once every action is completed,
   // so its step only exists while the section is rendered.
   if (visibility.showRecommendedActions) {
      steps.push({
         icon: tourIcon("list-check"),
         title: t("tour.recommendedActions.title"),
         content: t("tour.recommendedActions.content"),
         selector: "#tour-recommended-actions",
         side: "left",
         showControls: true,
         showSkip: true,
         pointerPadding: 10,
         pointerRadius: 12,
         viewportID: "dashboard-viewport",
      });
   }

   if (showCommunicationsSection) {
      steps.push({
         icon: tourIcon("tower-broadcast"),
         title: t("tour.communicationsSection.title"),
         content: t("tour.communicationsSection.content"),
         selector: "#tour-section-communications",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showInbound) {
      steps.push({
         icon: tourIcon("arrow-right-to-arc"),
         title: t("tour.inbound.title"),
         content: t("tour.inbound.content"),
         selector: "#tour-nav-inbound",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showOutreach) {
      steps.push({
         icon: tourIcon("arrow-left-to-arc"),
         title: t("tour.outreach.title"),
         content: t("tour.outreach.content"),
         selector: "#tour-nav-outreach",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showContacts) {
      steps.push({
         icon: tourIcon("address-card"),
         title: t("tour.contacts.title"),
         content: t("tour.contacts.content"),
         selector: "#tour-nav-contacts",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showCampaigns) {
      steps.push({
         icon: tourIcon("megaphone"),
         title: t("tour.campaigns.title"),
         content: t("tour.campaigns.content"),
         selector: "#tour-nav-campaigns",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showGailGpt) {
      steps.push({
         icon: tourIcon("comment"),
         title: t("tour.gailgptSection.title"),
         content: t("tour.gailgptSection.content"),
         selector: "#tour-nav-gailgpt",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   if (visibility.showGeneralSection) {
      steps.push({
         icon: tourIcon("gear"),
         title: t("tour.generalSection.title"),
         content: t("tour.generalSection.content"),
         selector: "#tour-section-general",
         side: "right",
         showControls: true,
         showSkip: true,
         pointerPadding: 4,
         pointerRadius: 8,
      });
   }

   steps.push({
      icon: tourIcon("party-horn"),
      title: t("tour.complete.title"),
      content: t("tour.complete.content"),
      showControls: true,
      showSkip: false,
   });

   return [{tour: DASHBOARD_TOUR_NAME, steps}];
}
